import { RoleName } from "@/api/users-types";
import { Link } from "@/components/ui/Link";
import { QaToolHeader } from "@/components/ui/QaToolHeader";
import { SIDEBAR_WIDTH } from "@/const";
import { hasRole } from "@/hooks/use-has-role";
import { useLogout, useUser } from "@/queries/auth";
import { useSidebarStore } from "@/stores/sidebar-store";
import {
  AddBoxOutlined,
  AnalyticsOutlined,
  AutoFixHighOutlined,
  AutoStoriesOutlined,
  Check,
  ChecklistRtlOutlined,
  ComputerOutlined,
  DarkModeOutlined,
  EngineeringOutlined,
  LightModeOutlined,
  LogoutOutlined,
  MoreVertOutlined,
  PublishOutlined,
  SettingsSuggestOutlined,
  WorkspacesOutlined,
} from "@mui/icons-material";
import {
  Avatar,
  Button,
  Container,
  Divider,
  Drawer,
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useColorScheme,
} from "@mui/material";
import { useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router";
import { Fragment } from "react/jsx-runtime";
import { useIsLoginPage } from "./hooks/use-is-login-page";

export function Sidebar() {
  const isLoginPage = useIsLoginPage();

  const { open, disabled } = useSidebarStore();

  const { data: user } = useUser({ enabled: !isLoginPage });

  const canViewConfigsAndPrompts =
    !!user && hasRole(user, [RoleName.promptEngineer, RoleName.episodeManager]);

  // list of sections of navigation buttons
  const buttonConfig = useMemo(
    () => [
      {
        sectionTitle: "Episodes",
        visible: !!user,
        buttons: [
          {
            href: "/episodes",
            text: "Browse episodes",
            Icon: AutoStoriesOutlined,
          },
          {
            href: "/validate",
            text: "Validate",
            Icon: ChecklistRtlOutlined,
          },
          {
            href: "/submit",
            text: "Submit",
            Icon: PublishOutlined,
          },
        ],
      },
      {
        sectionTitle: "Analytics",
        visible: !!user && hasRole(user, RoleName.analyticsViewer),
        buttons: [
          {
            href: "/reports",
            text: "Reports",
            Icon: AnalyticsOutlined,
          },
        ],
      },
      {
        sectionTitle: "Image Editing",
        visible: !!user && hasRole(user, RoleName.imageEditor),
        buttons: [
          {
            href: "/manual-edit",
            text: "Manual edit",
            Icon: AutoFixHighOutlined,
          },
        ],
      },
      {
        sectionTitle: "Configs & Prompts",
        visible: canViewConfigsAndPrompts,
        buttons: [
          {
            href: "/prompts",
            text: "Prompts",
            Icon: EngineeringOutlined,
          },
          {
            href: "/story-configs",
            text: "Story configs",
            Icon: SettingsSuggestOutlined,
          },
          {
            href: "/master-combinations",
            text: "Master combinations",
            Icon: WorkspacesOutlined,
          },
        ],
      },
      {
        sectionTitle: "Story Generator",
        visible: !!user && hasRole(user, RoleName.episodeManager),
        buttons: [
          {
            href: "/story-generator",
            text: "Story generator",
            Icon: AddBoxOutlined,
          },
        ],
      },
    ],
    [user, canViewConfigsAndPrompts],
  );

  return (
    <Drawer
      variant={"persistent"}
      open={open && !disabled}
      sx={{
        width: SIDEBAR_WIDTH,
        "& .MuiDrawer-paper": {
          width: SIDEBAR_WIDTH,
        },
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          pb: 2,
        }}
      >
        <QaToolHeader
          sx={{
            height: 48, // align with AppBar height (more specifically, Toolbar inside AppBar height)
            mb: "1px", // visually align with AppBar bottom border
            display: "flex",
            alignItems: "center",
          }}
        />
        <Stack gap={1} flexGrow={1} component={"nav"}>
          {buttonConfig.map((section, sectionIdx) => {
            return (
              section.visible && (
                <Fragment key={sectionIdx}>
                  <Typography variant="overline">
                    {section.sectionTitle}
                  </Typography>
                  {section.buttons.map((button, buttonIdx) => {
                    return (
                      <Button
                        key={buttonIdx}
                        href={button.href}
                        variant="text"
                        startIcon={<button.Icon />}
                        sx={{
                          justifyContent: "flex-start",
                          textTransform: "none",
                          color: "text.primary",
                        }}
                        LinkComponent={(props) => (
                          <RouterLink {...props} to={props.href} />
                        )}
                      >
                        {button.text}
                      </Button>
                    );
                  })}
                </Fragment>
              )
            );
          })}
        </Stack>
        <UserMenu />
      </Container>
    </Drawer>
  );
}

function UserMenu() {
  const logout = useLogout();

  const isLoginPage = useIsLoginPage();

  const { data: user } = useUser({ enabled: !isLoginPage });

  const [menuOpen, setMenuOpen] = useState(false);

  const anchorRef = useRef<HTMLButtonElement>(null);

  const { mode, setMode } = useColorScheme();

  if (isLoginPage) {
    return null;
  }

  return (
    <Stack
      alignItems={"center"}
      justifyContent={"space-between"}
      direction={"row"}
      gap={2}
    >
      {user ? (
        <>
          <Stack direction="row" alignItems="center" gap={1}>
            <Avatar
              sx={{
                width: "1.75rem",
                height: "1.75rem",
              }}
            >
              {user.username[0].toUpperCase()}
            </Avatar>
            <Typography>{user.username}</Typography>
          </Stack>
          <IconButton ref={anchorRef} onClick={() => setMenuOpen(true)}>
            <MoreVertOutlined />
          </IconButton>
        </>
      ) : (
        <Link color="inherit" to="/login">
          Login
        </Link>
      )}
      <Menu
        anchorEl={anchorRef.current}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{
          "& .MuiPaper-root": {
            minWidth: 240,
          },
        }}
      >
        <ListSubheader sx={{ bgcolor: "var(--Paper-overlay)" }}>
          Theme
        </ListSubheader>
        <MenuItem
          onClick={() => {
            setMode("light");
            setMenuOpen(false);
          }}
          sx={{ display: "flex", gap: 1, alignItems: "center" }}
        >
          <LightModeOutlined />
          <Typography sx={{ flexGrow: 1 }}>Light</Typography>
          {mode === "light" ? <Check /> : null}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMode("dark");
            setMenuOpen(false);
          }}
          sx={{ display: "flex", gap: 1, alignItems: "center" }}
        >
          <DarkModeOutlined />
          <Typography sx={{ flexGrow: 1 }}>Dark</Typography>
          {mode === "dark" ? <Check /> : null}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMode("system");
            setMenuOpen(false);
          }}
          sx={{ display: "flex", gap: 1, alignItems: "center" }}
        >
          <ComputerOutlined />
          <Typography sx={{ flexGrow: 1 }}>System</Typography>
          {mode === "system" || !mode ? <Check /> : null}
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            logout();
            setMenuOpen(false);
          }}
          sx={{ display: "flex", gap: 1, alignItems: "center" }}
        >
          <LogoutOutlined />
          <Typography>Logout</Typography>
        </MenuItem>
      </Menu>
    </Stack>
  );
}
